import { authService } from "./auth";
import { store } from "../state/store";
import { logout } from "../state/slices/authSlice";
import { parseBranchSwitchingFlag } from "../types/user";

// Read BASE_URL from environment variable
// In development, use relative URLs to leverage Vite proxy (bypasses CORS)
// In production, use the full API server URL or environment variable
const BASE_URL = import.meta.env.DEV ? "" : (import.meta.env.VITE_API_BASE_URL || "");

// Debug: Log BASE_URL in development (remove in production)


// ==================== Type Definitions ====================

export interface LoginUserPayload {
  branch_id?: string;
  branch_name?: string;
  branch_switching?: number;
  [key: string]: any;
}

export interface GenericModulePayload {
  modulename: string;
  skip: number;
  limit: number;
  search: Array<{ value: string; name: string; controltype: string }>;
  sort: Array<{ field: string; direction: "asc" | "desc" }>;
  view?: string; // Database view name
  filter?: string; // SQL filter condition
  loginUser?: LoginUserPayload;
}

export interface GenericModuleResponse {
  success: boolean;
  totalcount?: number;
  data: Record<string, any>[];
}

export interface LookupOption {
  label: string;
  value: string | number;
  disabled?: boolean;
  icon?: string;
}

export interface LookupResponse {
  success: boolean;
  data?: LookupOption[] | Record<string, any>[];
  [key: string]: any;
}

export interface LookupPayload {
  parentid: string | number;
  lookuptype: string;
  /** When set in screen settings, forwarded to /pabws/utility lookups. */
  accountMasterKey?: string;
  /** Server-side search filter for Search_Select lookups. */
  search_arg?: string;
  /**
   * Currently selected option id. For Search_Select, backend appends this row
   * when it is missing from the paginated top-10 results (edit-mode display).
   */
  selectedid?: string;
  /**
   * Extra id to include in Search_Select results (add/prepopulate). Same append
   * behavior as selectedid; use when selectedid should stay empty.
   * Discarded when equal to selectedid.
   */
  additionalSelectedId?: string;
  limit?: number; // Optional limit - if not provided or 0, backend should return all results
  skip?: number; // Optional skip for pagination
  /** Current branch for branch-scoped lookup filters (<branch_id>). */
  branch_id?: string;
}

// ==================== Dynamic API Configuration ====================

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export type ResponseType =
  | "json"
  | "text"
  | "blob"
  | "arrayBuffer"
  | "formData";

export type ErrorHandlingStrategy =
  | "throw" // Throw error immediately
  | "return-null" // Return null on error
  | "return-empty" // Return empty object/array
  | "custom"; // Use custom error handler

export interface RequestConfig {
  // Headers configuration
  headers?: Record<string, string>;
  includeAuthToken?: boolean; // Automatically include auth token from localStorage
  authTokenKey?: string; // Key to get token from (default: "encryptedData" or "encryptedUserData")
  authHeaderType?: "Bearer" | "Token" | "Custom"; // How to format auth header
  authHeaderName?: string; // Custom header name for auth (default: "Authorization")

  // Request configuration
  timeout?: number; // Request timeout in milliseconds
  retries?: number; // Number of retry attempts
  retryDelay?: number; // Delay between retries in milliseconds
  retryCondition?: (error: any) => boolean; // Function to determine if request should be retried

  // Payload transformation
  transformPayload?: (payload: any) => any; // Transform payload before sending
  transformParams?: (params: Record<string, any>) => Record<string, any>; // Transform query params

  // Response handling
  responseType?: ResponseType; // How to parse response
  transformResponse?: (response: any) => any; // Transform response after parsing
  validateResponse?: (response: any) => boolean; // Validate response structure

  // Error handling
  errorHandling?: ErrorHandlingStrategy;
  onError?: (error: any, config: RequestConfig) => any; // Custom error handler
  onSuccess?: (response: any) => void; // Success callback

  // Request options
  signal?: AbortSignal; // Abort signal for cancellation
  credentials?: RequestCredentials; // CORS credentials
  mode?: RequestMode; // CORS mode
  cache?: RequestCache; // Cache mode
}

export interface ApiRequestOptions extends RequestConfig {
  endpoint: string;
  method?: HttpMethod;
  payload?: any;
  params?: Record<string, any>;
  baseUrl?: string; // Override default BASE_URL
}

// ==================== Helper Functions ====================

/**
 * Preferred storage keys that should already contain raw tokens
 */
const DIRECT_TOKEN_STORAGE_KEYS = [
  "authToken",
  "token",
  "jwtToken",
  "jwt_token",
  "accessToken",
  "access_token",
  "bearerToken",
  "bearer_token",
  "sessionToken",
  "session_token",
];

/**
 * Storage keys that contain encrypted user blobs which may hold the token
 */
const ENCRYPTED_STORAGE_KEYS = ["encryptedUserData", "encryptedData"];

const TOKEN_FIELD_CANDIDATES = new Set(
  [
    "authToken",
    "token",
    "jwtToken",
    "jwt_token",
    "accessToken",
    "access_token",
    "bearerToken",
    "bearer_token",
    "sessionToken",
    "session_token",
    "Authorization",
    "authorization",
  ].map((key) => key.toLowerCase())
);

const normalizeTokenValue = (value: string): string =>
  value.replace(/^Bearer\s+/i, "").trim();

/**
 * Check if a string is a URL (http/https/ftp/etc)
 */
const isUrl = (value: string): boolean => {
  if (!value || typeof value !== "string") return false;
  try {
    // Check for common URL patterns
    const urlPattern = /^(https?|ftp|file|data):\/\//i;
    if (urlPattern.test(value.trim())) return true;

    // Try to parse as URL
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

/**
 * Validate if a string is a valid authentication token
 * Rejects URLs and ensures it's a proper token format
 */
const isValidToken = (token: string): boolean => {
  if (!token || typeof token !== "string") return false;

  // Reject URLs
  if (isUrl(token)) return false;

  // Reject empty or whitespace-only tokens
  if (!token.trim()) return false;

  // Accept JWT tokens (3 segments separated by dots)
  if (isLikelyJwt(token)) return true;

  // Accept tokens that are at least 20 characters and don't look like URLs
  // This allows for custom token formats while rejecting URLs
  if (token.length >= 20 && !isUrl(token)) {
    // Additional check: tokens shouldn't contain common URL characters in sequence
    const urlLikePattern = /\/\//;
    if (!urlLikePattern.test(token)) {
      return true;
    }
  }

  return false;
};

const isLikelyJwt = (token: string): boolean => {
  if (!token) return false;
  const segments = token.split(".");
  return segments.length >= 3 && segments.every((part) => part.length > 0);
};

const extractTokenFromObject = (
  source: unknown,
  visited = new WeakSet<object>()
): string | null => {
  if (!source || typeof source !== "object") {
    return null;
  }

  if (visited.has(source as object)) {
    return null;
  }
  visited.add(source as object);

  const record = source as Record<string, any>;

  const candidates: string[] = [];

  for (const [key, rawValue] of Object.entries(record)) {
    if (typeof rawValue === "string") {
      const normalized = normalizeTokenValue(rawValue);
      if (!normalized) continue;

      // Only consider valid tokens (not URLs)
      if (isValidToken(normalized)) {
        if (
          TOKEN_FIELD_CANDIDATES.has(key.toLowerCase()) ||
          /token|authorization/i.test(key) ||
          isLikelyJwt(normalized)
        ) {
          candidates.push(normalized);
        }
      }
    }
  }

  // Prefer JWT tokens
  const preferred = candidates.find(isLikelyJwt);
  if (preferred) {
    return preferred;
  }

  // Return first valid token candidate (already validated by isValidToken)
  if (candidates.length > 0) {
    return candidates[0];
  }

  for (const value of Object.values(record)) {
    if (value && typeof value === "object") {
      const nested = extractTokenFromObject(value, visited);
      if (nested) {
        return nested;
      }
    }
  }

  return null;
};

/**
 * Get authentication token from localStorage
 */
const getAuthToken = (tokenKey?: string): string | null => {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return null;
  }

  const normalizedKeyList = tokenKey
    ? [tokenKey]
    : [...DIRECT_TOKEN_STORAGE_KEYS, ...ENCRYPTED_STORAGE_KEYS];

  let fallbackToken: string | null = null;

  for (const key of normalizedKeyList) {
    const rawValue = localStorage.getItem(key);
    if (!rawValue) continue;

    const treatAsEncrypted = ENCRYPTED_STORAGE_KEYS.includes(key);

    if (!treatAsEncrypted) {
      const normalized = normalizeTokenValue(rawValue);
      // Only accept valid tokens (not URLs)
      if (isValidToken(normalized)) {
        if (isLikelyJwt(normalized)) {
          return normalized;
        }

        if (!fallbackToken) {
          fallbackToken = normalized;
        }
      }
      continue;
    }

    try {
      const decrypted = authService.decryptData(rawValue);
      if (decrypted && typeof decrypted === "object") {
        const extracted = extractTokenFromObject(decrypted);
        if (extracted && isValidToken(extracted)) {
          if (isLikelyJwt(extracted)) {
            try {
              localStorage.setItem("authToken", extracted);
            } catch {
              // noop - best effort cache only
            }
            return extracted;
          }

          if (!fallbackToken) {
            fallbackToken = extracted;
          }
        }
      }
    } catch (error) {

    }
  }

  return fallbackToken;
};

export const resolveLoginUser = (): LoginUserPayload | undefined => {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return undefined;
  }

  try {
    const encryptedUserData =
      localStorage.getItem("encryptedUserData") ||
      localStorage.getItem("encryptedData");

    if (!encryptedUserData) {
      return undefined;
    }

    const decrypted = authService.decryptData(encryptedUserData);
    if (!decrypted || typeof decrypted !== "object") {
      return undefined;
    }

    const record = decrypted as Record<string, any>;

    const resolveBranchSwitching = (): number | undefined => {
      const loginUserRecord =
        record.loginUser && typeof record.loginUser === "object"
          ? (record.loginUser as Record<string, unknown>)
          : null;
      const raw =
        record.branch_switching ??
        record.branchSwitching ??
        record.branch_switch ??
        record.branchSwitch ??
        record.branch_switching_flag ??
        loginUserRecord?.branch_switching ??
        loginUserRecord?.branchSwitching;
      if (raw === undefined || raw === null) {
        return undefined;
      }
      return parseBranchSwitchingFlag(raw);
    };

    if (record.loginUser && typeof record.loginUser === "object") {
      const loginUser = {
        ...(record.loginUser as LoginUserPayload),
      };
      const branchSwitching = resolveBranchSwitching();
      if (branchSwitching !== undefined) {
        loginUser.branch_switching = branchSwitching;
      }
      return loginUser;
    }

    const getValue = (keys: string[]): any => {
      for (const key of keys) {
        if (
          key in record &&
          record[key] !== undefined &&
          record[key] !== null
        ) {
          return record[key];
        }
      }
      return undefined;
    };

    const branchId = getValue([
      "branch_id",
      "branchId",
      "transaction_branch_id",
      "transactionBranchId",
      "transaction_branchid",
    ]);

    const branchName = getValue([
      "branch_name",
      "branchName",
      "branch",
      "branch_desc",
      "branchDescription",
    ]);

    const branchSwitchingRaw = getValue([
      "branch_switching",
      "branchSwitching",
      "branch_switch",
      "branchSwitch",
      "branch_switching_flag",
    ]);

    const branch_switching =
      branchSwitchingRaw === undefined || branchSwitchingRaw === null
        ? undefined
        : parseBranchSwitchingFlag(branchSwitchingRaw);

    const employeeId = getValue([
      "employee_id",
      "employeeId",
      "empId",
    ]);

    const userId = getValue([
      "userid",
      "userId",
      "id",
    ]);

    const loginUser: LoginUserPayload = {
      branch_id: branchId ?? "",
      branch_name: branchName ?? "",
    };

    if (branch_switching !== undefined) {
      loginUser.branch_switching = branch_switching;
    }

    if (employeeId) {
      loginUser.employee_id = employeeId;
    }
    if (userId) {
      loginUser.userid = userId;
    }

    return loginUser;
  } catch (error) {

    return undefined;
  }
};

/**
 * Build headers with authentication
 */
const buildHeaders = (config: RequestConfig): Record<string, string> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...config.headers,
  };

  // Add authentication token if requested
  if (config.includeAuthToken) {
    const token = getAuthToken(config.authTokenKey);
    if (token && isValidToken(token)) {
      const headerName = config.authHeaderName || "Authorization";
      const headerType = config.authHeaderType || "Bearer";

      if (headerType === "Bearer") {
        headers[headerName] = `Bearer ${token}`;
      } else if (headerType === "Token") {
        headers[headerName] = `Token ${token}`;
      } else if (headerType === "Custom") {
        headers[headerName] = token;
      } else {
        headers[headerName] = token;
      }

      // Also add token as separate header for backward compatibility
      if (!headers.token && !headers.Token) {
        headers.token = token;
      }
    } else if (config.includeAuthToken && !token) {

    }
  }

  return headers;
};

/**
 * Parse response based on response type
 */
const parseResponse = async (
  response: Response,
  responseType: ResponseType = "json"
): Promise<any> => {
  switch (responseType) {
    case "json":
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        return await response.json();
      } else {
        const text = await response.text();
        try {
          return JSON.parse(text);
        } catch {

          return text;
        }
      }
    case "text":
      return await response.text();
    case "blob":
      return await response.blob();
    case "arrayBuffer":
      return await response.arrayBuffer();
    case "formData":
      return await response.formData();
    default:
      return await response.json();
  }
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Core dynamic request handler
 */
const makeRequest = async <T = any>(options: ApiRequestOptions): Promise<T> => {
  const {
    endpoint,
    method = "GET",
    payload,
    params,
    baseUrl: providedBaseUrl,
    timeout = 30000,
    retries = 0,
    retryDelay = 1000,
    retryCondition,
    transformPayload,
    transformParams,
    responseType = "json",
    transformResponse,
    validateResponse,
    errorHandling = "throw",
    onError,
    onSuccess,
    signal,
    credentials,
    mode,
    cache,
    ...config
  } = options;

  // In development, force empty baseUrl to use proxy unless explicitly provided
  const baseUrl =
    import.meta.env.DEV && providedBaseUrl === undefined
      ? ""
      : providedBaseUrl !== undefined
        ? providedBaseUrl
        : BASE_URL;

  let attempt = 0;
  const maxAttempts = retries + 1;

  while (attempt < maxAttempts) {
    try {
      // Build URL - ensure relative URLs in development to use proxy
      let url: string;
      if (import.meta.env.DEV && !baseUrl) {
        // In development with no baseUrl, use relative URL to leverage Vite proxy
        url = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
      } else {
        // In production or when baseUrl is explicitly set, use full URL
        url = `${baseUrl}${endpoint}`;
      }

      // Debug logging in development


      // Transform and add query parameters
      if (params && Object.keys(params).length > 0) {
        const transformedParams = transformParams
          ? transformParams(params)
          : params;

        const queryString = new URLSearchParams(
          Object.entries(transformedParams).reduce((acc, [key, value]) => {
            if (value !== null && value !== undefined) {
              acc[key] = String(value);
            }
            return acc;
          }, {} as Record<string, string>)
        ).toString();

        if (queryString) {
          url += `?${queryString}`;
        }
      }

      // Transform payload
      let finalPayload = payload;
      if (payload && transformPayload) {
        finalPayload = transformPayload(payload);
      }

      // Build headers
      const headers = buildHeaders(config);

      // Create fetch options
      const fetchOptions: RequestInit = {
        method,
        headers,
        signal,
        // Use 'same-origin' credentials by default (works with proxy)
        // Can be overridden via config.credentials if needed
        credentials: credentials || "same-origin",
        // Use 'same-origin' mode when using relative URLs (proxy), 'cors' for absolute URLs
        mode:
          mode || (import.meta.env.DEV && !BASE_URL ? "same-origin" : "cors"),
        cache,
      };

      // Add body for non-GET requests
      if (method !== "GET" && finalPayload !== undefined) {
        if (headers["Content-Type"]?.includes("application/json")) {
          fetchOptions.body = JSON.stringify(finalPayload);
        } else if (finalPayload instanceof FormData) {
          fetchOptions.body = finalPayload;
          // Remove Content-Type header to let browser set it with boundary
          delete headers["Content-Type"];
        } else {
          fetchOptions.body = finalPayload;
        }
      }

      // Create timeout controller
      const controller = new AbortController();
      const timeoutId = timeout
        ? setTimeout(() => controller.abort(), timeout)
        : null;

      // Combine abort signals
      if (signal) {
        signal.addEventListener("abort", () => controller.abort());
      }

      // Make request
      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
      });

      // Clear timeout
      if (timeoutId) clearTimeout(timeoutId);

      // Handle non-ok responses
      if (!response.ok) {
        const errorText = await response.text();
        const errorMessage = errorText.substring(0, 500); // Show more error details
        const error = new Error(
          `HTTP error! status: ${response.status}, message: ${errorMessage}`
        );
        (error as any).status = response.status;
        (error as any).response = errorText;

        // Enhanced error logging in development
        if (import.meta.env.DEV) {
          console.error("[API Error Details]:", {
            url,
            status: response.status,
            message: errorMessage,
          });
        }

        // Handle auth errors globally
        if (response.status === 401 || response.status === 403) {
          // Dispatch logout to clear state and trigger redirect
          store.dispatch(logout());
        }


        // Check if should retry
        if (attempt < maxAttempts - 1) {
          const shouldRetry = retryCondition
            ? retryCondition(error)
            : response.status >= 500 || response.status === 429;

          if (shouldRetry) {
            attempt++;
            await sleep(retryDelay * attempt); // Exponential backoff
            continue;
          }
        }

        throw error;
      }

      // Parse response
      let data = await parseResponse(response, responseType);

      // Transform response
      if (transformResponse) {
        data = transformResponse(data);
      }

      // Validate response
      if (validateResponse && !validateResponse(data)) {
        throw new Error("Response validation failed");
      }

      // Success callback
      if (onSuccess) {
        onSuccess(data);
      }

      return data as T;
    } catch (error: any) {
      // Handle abort/timeout
      if (error.name === "AbortError") {
        const abortError = new Error("Request timeout or aborted");
        if (errorHandling === "throw") throw abortError;
        if (errorHandling === "return-null") return null as T;
        if (errorHandling === "return-empty") return {} as T;
        if (onError) return onError(abortError, config);
        throw abortError;
      }

      // Check if should retry
      if (attempt < maxAttempts - 1) {
        const shouldRetry = retryCondition
          ? retryCondition(error)
          : error.status >= 500 || error.status === 429;

        if (shouldRetry) {
          attempt++;
          await sleep(retryDelay * attempt);
          continue;
        }
      }

      // Handle error based on strategy


      if (errorHandling === "throw") {
        throw error;
      }

      if (errorHandling === "return-null") {
        return null as T;
      }

      if (errorHandling === "return-empty") {
        return (Array.isArray(payload) ? [] : {}) as T;
      }

      if (errorHandling === "custom" && onError) {
        return onError(error, config);
      }

      throw error;
    }
  }

  throw new Error("Max retry attempts reached");
};

// ==================== API Service ====================

export const apiService = {
  /**
   * Core dynamic request method - handles all HTTP methods
   * @param options - Complete request configuration
   * @returns Promise with the response data
   */
  async request<T = any>(options: ApiRequestOptions): Promise<T> {
    return makeRequest<T>(options);
  },

  /**
   * Dynamic GET request method
   * @param endpoint - The API endpoint
   * @param params - Query parameters as an object
   * @param config - Optional request configuration
   * @returns Promise with the response data
   */
  async get<T = any>(
    endpoint: string,
    params?: Record<string, any>,
    config?: RequestConfig
  ): Promise<T> {
    return makeRequest<T>({
      endpoint,
      method: "GET",
      params,
      ...config,
    });
  },

  /**
   * Dynamic POST request method
   * @param endpoint - The API endpoint
   * @param payload - The request payload
   * @param config - Optional request configuration
   * @returns Promise with the response data
   */
  async post<T = any>(
    endpoint: string,
    payload?: any,
    config?: RequestConfig
  ): Promise<T> {
    return makeRequest<T>({
      endpoint,
      method: "POST",
      payload,
      ...config,
    });
  },

  /**
   * Dynamic PUT request method
   * @param endpoint - The API endpoint
   * @param payload - The request payload
   * @param config - Optional request configuration
   * @returns Promise with the response data
   */
  async put<T = any>(
    endpoint: string,
    payload?: any,
    config?: RequestConfig
  ): Promise<T> {
    return makeRequest<T>({
      endpoint,
      method: "PUT",
      payload,
      ...config,
    });
  },

  /**
   * Dynamic DELETE request method
   * @param endpoint - The API endpoint
   * @param config - Optional request configuration
   * @returns Promise with the response data
   */
  async delete<T = any>(endpoint: string, config?: RequestConfig): Promise<T> {
    return makeRequest<T>({
      endpoint,
      method: "DELETE",
      ...config,
    });
  },

  /**
   * Dynamic PATCH request method
   * @param endpoint - The API endpoint
   * @param payload - The request payload
   * @param config - Optional request configuration
   * @returns Promise with the response data
   */
  async patch<T = any>(
    endpoint: string,
    payload?: any,
    config?: RequestConfig
  ): Promise<T> {
    return makeRequest<T>({
      endpoint,
      method: "PATCH",
      payload,
      ...config,
    });
  },

  /**
   * Get screen configuration by ID
   * @param screenId - The ID of the screen to fetch
   * @param params - Optional extra query params (e.g. employee_id for screen 2217)
   * @returns Promise with the screen configuration
   */
  async getScreenConfig(
    screenId: string,
    params?: Record<string, any>
  ): Promise<any> {
    return this.get<any>(
      "/pabws/screens/get_screens_with_id",
      { screenid: screenId, ...(params || {}) }
    );
  },

  /**
   * Fetch lookup data for dropdowns using POST with payload
   * @param lookupName - The name of the lookup (e.g., "vehicle_make", "customers")
   * @param payload - The lookup payload with parentid and lookuptype
   * @param config - Optional request configuration
   * @returns Promise with lookup options
   */
  async getLookupData(
    lookupName: string,
    payload: LookupPayload,
    config?: RequestConfig
  ): Promise<LookupOption[]> {
    try {
      // Build the URL with lookupName as a path parameter
      const endpoint = `/pabws/utility/${lookupName}`;

      // Log API call for get_all_ledgers
      if (lookupName === "get_all_ledgers") {
        console.log(`[get_all_ledgers] API Call - Endpoint: ${endpoint}`, {
          payload,
          timestamp: new Date().toISOString(),
        });
      }

      // Ensure branch_id is present even if caller skipped buildLookupPayload.
      const requestPayload: LookupPayload = { ...payload };
      if (!requestPayload.branch_id) {
        const branchId = String(resolveLoginUser()?.branch_id ?? "").trim();
        if (branchId) {
          requestPayload.branch_id = branchId;
        }
      }

      const response = await this.post<LookupOption[]>(endpoint, requestPayload, {
        errorHandling: "return-empty",
        transformResponse: (data: any) => {
          // Log raw response for get_all_ledgers and journal_ledgers
          if (lookupName === "get_all_ledgers" || lookupName === "journal_ledgers") {
            console.log(`[${lookupName}] Raw API Response:`, {
              data,
              dataType: typeof data,
              isArray: Array.isArray(data),
              hasDataProperty: data?.data !== undefined,
              timestamp: new Date().toISOString(),
            });
          }
          // CRITICAL FIX: Handle encrypted response data
          if (data && data.data && typeof data.data === "string") {
            try {
              const decrypted = authService.decryptData(data.data);
              console.log("Decrypted----- -- - 1 - ----- data:", decrypted);
              if (decrypted) {
                if (import.meta.env.DEV) {
                  //  console.log(`[getLookupData] Decrypted response for ${lookupName}`);
                }
                data.data = decrypted;
              }
            } catch (e) {
              console.warn(`[getLookupData] Failed to decrypt data for ${lookupName}`, e);
            }
          }

          // Handle different response structures
          let lookupData: any[] = [];
          if (data) {
            if (data.data) {
              lookupData = Array.isArray(data.data) ? data.data : [data.data];
            } else if (Array.isArray(data)) {
              lookupData = data;
            } else {
              // If response is an object, try to extract data
              lookupData = [data];
            }
          }

          // Log response structure for get_all_ledgers to check for pagination
          if (lookupName === "get_all_ledgers") {
            console.log(`[get_all_ledgers] Response Structure:`, {
              hasData: !!data,
              hasDataProperty: !!data?.data,
              isArray: Array.isArray(data),
              dataKeys: data ? Object.keys(data) : [],
              lookupDataCount: lookupData.length,
              totalcount: data?.totalcount,
              hasMore: data?.hasMore,
              pagination: data?.pagination,
              fullResponse: data,
            });
          }

          // Transform lookup data to DropdownOption format
          // The API might return data in different formats, so we handle common cases
          // Debug: Log first item structure for troubleshooting (only in development)
          if (import.meta.env.DEV && lookupData.length > 0) {
            const sampleItem = lookupData[0];
            console.log(
              // `[getLookupData] ${lookupName} - Sample item structure:`,
              {
                keys: Object.keys(sampleItem),
                sample: sampleItem,
                // Test isIdLike on all string fields
                fieldTests: Object.keys(sampleItem)
                  .map((k) => {
                    const val = sampleItem[k];
                    if (typeof val === "string") {
                      // Test the isIdLike logic inline
                      const str = String(val).trim();
                      const isUuid =
                        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(
                          str
                        );
                      const isUuidWithUnderscore =
                        /^[0-9a-f-]+_[0-9_]+/i.test(str) && str.length > 30;
                      const isLongHex =
                        str.length > 50 && /^[0-9a-f_-]+$/i.test(str);
                      return {
                        field: k,
                        value: val,
                        isIdLike: isUuid || isUuidWithUnderscore || isLongHex,
                        tests: { isUuid, isUuidWithUnderscore, isLongHex },
                      };
                    }
                    return null;
                  })
                  .filter(Boolean),
              }
            );
          }

          // Special handling for get_all_ledgers API response structure {id, name}
          // This ensures proper transformation for this specific API
          const isGetAllLedgers = lookupName === "get_all_ledgers";

          const options: LookupOption[] = lookupData.map(
            (item: any, index: number) => {
              // For get_all_ledgers and journal_ledgers, use direct mapping if structure is {id, name}
              const isJournalLedgers = lookupName === "journal_ledgers";
              if (
                (isGetAllLedgers || isJournalLedgers) &&
                item.id !== undefined &&
                item.name !== undefined
              ) {
                const transformedItem = {
                  label: String(item.name).trim(),
                  value: item.id,
                  disabled: item.disabled || false,
                  icon: item.icon,
                };

                // Log first few items for debugging, or all items for journal_ledgers
                if (index < 3 || lookupName === "journal_ledgers") {
                  console.log(
                    `[${lookupName}] Direct mapping item ${index}:`,
                    {
                      original: item,
                      transformed: transformedItem,
                    }
                  );
                }

                return transformedItem;
              }

              // Helper function to check if a value looks like a UUID or ID (should not be used as label)
              const isIdLike = (val: any): boolean => {
                if (!val || typeof val !== "string") return false;
                const str = String(val).trim();
                if (str.length === 0) return false;

                // Check for UUID pattern (with dashes) - matches at the start
                // Pattern: 8-4-4-4-12 hex digits with dashes
                // Example: "75ffdbdb-115b-4dbf-950d-1e837e4afe10"
                if (
                  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(
                    str
                  )
                ) {
                  return true;
                }

                // Check for UUID-like pattern followed by underscore and numbers
                // Pattern: starts with hex chars (possibly with dashes), then underscore, then numbers/underscores
                // This catches IDs like: "75ffdbdb-115b-4dbf-950d-1e837e4afe10_000001_251128104842859_3"
                if (/^[0-9a-f-]+_[0-9_]+/i.test(str) && str.length > 30) {
                  return true;
                }

                // Check for long alphanumeric strings with underscores (like the IDs in the image)
                // Pattern: UUID-like with underscores and numbers (exact match)
                if (/^[0-9a-f-]+_[0-9_]+$/i.test(str) && str.length > 30) {
                  return true;
                }

                // Check for long numeric strings (likely IDs)
                if (/^[0-9]+$/.test(str) && str.length > 10) {
                  return true;
                }

                // Check for very long strings that look like IDs (more than 50 chars with hex/underscores/dashes)
                if (str.length > 50 && /^[0-9a-f_-]+$/i.test(str)) {
                  return true;
                }

                // Additional check: if string contains a UUID pattern anywhere and is longer than 36 chars
                // This catches cases where UUID is embedded in a longer string
                if (
                  str.length > 36 &&
                  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(
                    str
                  )
                ) {
                  return true;
                }

                return false;
              };

              // Helper function to get a field value if it's not ID-like
              const getFieldValue = (fieldName: string): string | null => {
                const value = item[fieldName];
                if (value !== undefined && value !== null && !isIdLike(value)) {
                  return String(value).trim();
                }
                return null;
              };

              // Helper function to find a name field that's not ID-like
              const findNameField = (): string | null => {
                const allKeys = Object.keys(item);

                // First, try explicit common field names
                const explicitFields = [
                  "label",
                  "name",
                  "item_name",
                  "itemname",
                  "itemName",
                  "text",
                  "description",
                  "display_name",
                  "displayName",
                  "product_name",
                  "productname",
                  "productName",
                  "item_description",
                  "itemdesc",
                  "itemDesc",
                ];

                for (const field of explicitFields) {
                  const value = getFieldValue(field);
                  if (value && value.length > 0) {
                    return value;
                  }
                }

                // Then try fields ending with "name" (prioritize these)
                const nameFields = allKeys.filter((k) => {
                  const lowerKey = k.toLowerCase();
                  return (
                    (lowerKey.endsWith("name") || lowerKey === "name") &&
                    !lowerKey.includes("id") &&
                    !isIdLike(item[k])
                  );
                });

                for (const field of nameFields) {
                  const value = getFieldValue(field);
                  if (value && value.length > 0) {
                    return value;
                  }
                }

                // Try fields containing "name" (but not ending with "id")
                const nameLikeFields = allKeys.filter((k) => {
                  const lowerKey = k.toLowerCase();
                  return (
                    lowerKey.includes("name") &&
                    !lowerKey.endsWith("id") &&
                    !lowerKey.includes("guid") &&
                    !isIdLike(item[k])
                  );
                });

                for (const field of nameLikeFields) {
                  const value = getFieldValue(field);
                  if (value && value.length > 0) {
                    return value;
                  }
                }

                // Try description/text fields
                const descFields = allKeys.filter((k) => {
                  const lowerKey = k.toLowerCase();
                  return (
                    (lowerKey.includes("desc") || lowerKey.includes("text")) &&
                    !lowerKey.includes("id") &&
                    !isIdLike(item[k])
                  );
                });

                for (const field of descFields) {
                  const value = getFieldValue(field);
                  if (value && value.length > 0) {
                    return value;
                  }
                }

                // Try code field if it doesn't look like an ID
                if (item.code && !isIdLike(item.code)) {
                  return String(item.code).trim();
                }

                // Last resort: find any string field that's not ID-like and not an ID field
                const stringFields = allKeys.filter((k) => {
                  const lowerKey = k.toLowerCase();
                  const value = item[k];
                  return (
                    typeof value === "string" &&
                    value.length > 0 &&
                    !lowerKey.includes("id") &&
                    !lowerKey.includes("guid") &&
                    !lowerKey.includes("uuid") &&
                    !isIdLike(value)
                  );
                });

                if (stringFields.length > 0) {
                  return String(item[stringFields[0]]).trim();
                }

                return null;
              };

              // Get label - never use ID-like values
              let label = findNameField();

              // Debug: Log what findNameField returned
              if (import.meta.env.DEV) {
                const labelIsIdLike = label ? isIdLike(label) : false;
                console.log(
                  // `[getLookupData] ${lookupName} - Label detection result:`,
                  // {
                  //   label,
                  //   labelIsIdLike,
                  //   itemKeys: Object.keys(item),
                  //   itemSample: item,
                  // }
                );
              }

              // Double-check: if label is ID-like, try to find another field
              if (label && isIdLike(label)) {
                // Debug logging to see what happened
                if (import.meta.env.DEV) {
                  console.warn(
                    // `[getLookupData] ${lookupName} - Label detected as ID-like, searching for fallback:`,
                    // {
                    //   detectedLabel: label,
                    //   isIdLike: isIdLike(label),
                    //   availableKeys: Object.keys(item),
                    //   sampleItem: item,
                    // }
                  );
                }

                // Try to find any non-ID string field as last resort
                const allKeys = Object.keys(item);
                const fallbackField = allKeys.find((k) => {
                  const lowerKey = k.toLowerCase();
                  const val = item[k];
                  return (
                    typeof val === "string" &&
                    val.length > 0 &&
                    val.length < 100 && // Reasonable length
                    !lowerKey.includes("id") &&
                    !lowerKey.includes("guid") &&
                    !lowerKey.includes("uuid") &&
                    !isIdLike(val) &&
                    val !== label // Not the same as current label
                  );
                });
                if (fallbackField) {
                  label = String(item[fallbackField]).trim();
                  if (import.meta.env.DEV) {
                    console.log(
                      // `[getLookupData] ${lookupName} - Using fallback field:`,
                      // {
                      //   fallbackField,
                      //   fallbackValue: label,
                      // }
                    );
                  }
                } else {
                  label = "Unknown Item"; // Final fallback
                  // if (import.meta.env.DEV) {
                  //   console.warn(
                  //     `[getLookupData] ${lookupName} - No fallback field found, using "Unknown Item"`,
                  //     {
                  //       item,
                  //       allKeys,
                  //     }
                  //   );
                  // }
                }
              }

              // If we still don't have a label, use a placeholder instead of ID
              if (!label || label.length === 0) {
                label = "Unknown Item"; // Fallback instead of showing ID
              }

              // Final safety check: if label is still ID-like after all checks, use placeholder
              if (isIdLike(label)) {
                if (import.meta.env.DEV) {
                  console.error(
                    // `[getLookupData] ${lookupName} - Label is still ID-like after all checks, using "Unknown Item":`,
                    // {
                    //   label,
                    //   labelLength: label?.length,
                    //   isIdLikeResult: isIdLike(label),
                    //   item,
                    //   allItemKeys: Object.keys(item),
                    //   allItemValues: Object.entries(item).map(([k, v]) => ({
                    //     key: k,
                    //     value: v,
                    //     valueType: typeof v,
                    //     isIdLike: typeof v === "string" ? isIdLike(v) : false,
                    //   })),
                    // }
                  );
                }
                label = "Unknown Item";
              }

              const dynamicIdKey = Object.keys(item).find((k) => {
                const lowerKey = k.toLowerCase();
                return lowerKey.endsWith("id") || lowerKey.includes("id");
              });

              // Canonical ID used for resilient matching in SearchableDropdown.
              const canonicalId =
                item.id ||
                item.sup_id ||
                item.supplier_id ||
                item.vendor_id ||
                item.codeid ||
                item.value ||
                item.key ||
                item.code ||
                item[dynamicIdKey || ""] ||
                "";

              // Get value - keep existing behavior for most lookups (codeid-first),
              // but for supplier force canonical supplier id so edit prefill can match.
              let value: any =
                lookupName === "supplier"
                  ? canonicalId
                  : item.codeid || // Prioritize codeid - commonly used in SubModule dropdowns
                    item.value ||
                    item.id ||
                    item.key ||
                    item.code ||
                    item[dynamicIdKey || ""] ||
                    label;

              // Never pass a nested object as option.value (breaks MultiCheckBox / String() in payloads)
              if (value !== null && value !== undefined && typeof value === "object") {
                const o = value as Record<string, any>;
                const inner = o.id ?? o.value ?? o.codeid ?? o.key ?? o.code;
                value =
                  inner !== undefined && inner !== null && typeof inner !== "object"
                    ? inner
                    : "";
              }

              // Final check before returning - NEVER return UUID as label
              let finalLabel = String(label);

              // Aggressive check: if label is ID-like, force it to "Unknown Item"
              if (isIdLike(finalLabel)) {
                console.error(
                  `[getLookupData] ${lookupName} - CRITICAL: Attempting to return ID as label! Forcing to "Unknown Item"`,
                  {
                    finalLabel,
                    finalLabelLength: finalLabel.length,
                    isIdLikeResult: isIdLike(finalLabel),
                    item,
                    allKeys: Object.keys(item),
                    allItemValues: Object.entries(item).map(([k, v]) => ({
                      key: k,
                      value: typeof v === "string" ? v.substring(0, 100) : v,
                      valueType: typeof v,
                      isIdLike: typeof v === "string" ? isIdLike(v) : false,
                    })),
                  }
                );
                finalLabel = "Unknown Item";
              }

              // One more check: if label contains UUID pattern, it's an ID
              if (finalLabel && finalLabel.length > 30) {
                const hasUuidPattern =
                  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(
                    finalLabel
                  );
                if (hasUuidPattern) {
                  console.error(
                    `[getLookupData] ${lookupName} - Label contains UUID pattern! Forcing to "Unknown Item"`,
                    { finalLabel }
                  );
                  finalLabel = "Unknown Item";
                }
              }

              // Return LookupOption with all original properties preserved
              // This allows ExpressionV2 to access properties like purchase_rate, sale_rate, etc.
              // IMPORTANT: Spread item FIRST, then override with explicit properties
              // This ensures all original properties are preserved
              return {
                // Spread all original item properties FIRST
                ...item,
                // Add normalized id for components that match against option.id
                id: canonicalId,
                // Then override with our transformed values
                label: finalLabel,
                value: value,
                disabled: item.disabled || false,
                icon: item.icon,
              };
            }
          );

          return options;
        },
        ...config,
      });

      // Log final transformed options for get_all_ledgers
      if (lookupName === "get_all_ledgers") {
        console.log(`[get_all_ledgers] Transformed Options:`, {
          count: (response || []).length,
          options: response || [],
          sample: (response || []).slice(0, 5), // First 5 items
          timestamp: new Date().toISOString(),
        });
      }

      return response || [];
    } catch (error) {
      console.error(`Lookup ${lookupName} API error:`, error);
      if (lookupName === "get_all_ledgers") {
        console.error(`[get_all_ledgers] API Error Details:`, {
          error,
          errorMessage: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString(),
        });
      }
      return [];
    }
  },

  /**
   * Get raw lookup data without transformation (for SubModule)
   * @param lookupName - The lookup name
   * @param payload - The request payload
   * @param config - Optional request configuration
   * @returns Promise with raw data array
   */
  async getRawLookupData(
    lookupName: string,
    payload: LookupPayload,
    config?: RequestConfig
  ): Promise<any[]> {
    try {
      // Build the URL with lookupName as a path parameter
      const endpoint = `/pabws/utility/${lookupName}`;

      const requestPayload: LookupPayload = { ...payload };
      if (!requestPayload.branch_id) {
        const branchId = String(resolveLoginUser()?.branch_id ?? "").trim();
        if (branchId) {
          requestPayload.branch_id = branchId;
        }
      }

      const response = await this.post<any[]>(endpoint, requestPayload, {
        errorHandling: "return-empty",
        transformResponse: (data: any) => {
          // CRITICAL FIX: Handle encrypted response data
          if (data && data.data && typeof data.data === "string") {
            try {
              const decrypted = authService.decryptData(data.data);
              console.log("Decrypted -- - -- 2 ----- -- - - data:", decrypted);
              if (decrypted) {
                if (import.meta.env.DEV) {
                  console.log(`[getRawLookupData] Decrypted response for ${lookupName}`);
                }
                data.data = decrypted;
              }
            } catch (e) {
              console.warn(`[getRawLookupData] Failed to decrypt data for ${lookupName}`, e);
            }
          }

          // Handle different response structures but return raw data
          let lookupData: any[] = [];
          if (data) {
            if (data.data) {
              lookupData = Array.isArray(data.data) ? data.data : [data.data];
            } else if (Array.isArray(data)) {
              lookupData = data;
            } else {
              // If response is an object, try to extract data
              lookupData = [data];
            }
          }
          // Return raw data without transformation
          return lookupData;
        },
        ...config,
      });

      return response || [];
    } catch (error) {
      console.error(`Raw Lookup ${lookupName} API error:`, error);
      return [];
    }
  },

  /**
   * Generic method to fetch data from any module
   * @param moduleName - The name of the module (e.g., "vehicle_summary")
   * @param payload - The request payload with pagination, search, and sort parameters
   * @param config - Optional request configuration
   * @returns Promise with the module data
   */
  async getModuleData(
    moduleName: string,
    payload: Omit<GenericModulePayload, "modulename">,
    config?: RequestConfig
  ): Promise<GenericModuleResponse> {
    try {
      const requestPayload: GenericModulePayload = {
        modulename: moduleName,
        ...payload,
      };

      const defaultLoginUser = resolveLoginUser();
      if (defaultLoginUser) {
        requestPayload.loginUser = {
          ...defaultLoginUser,
          ...requestPayload.loginUser,
        };
      }

      const response = await this.post<GenericModuleResponse>(
        `/pabws/module/${moduleName}`,
        requestPayload,
        {
          errorHandling: "return-empty",
          transformResponse: (data: any) => {
            // Ensure response has expected structure
            if (!data || typeof data !== "object") {
              return {
                success: false,
                totalcount: 0,
                data: [],
              };
            }
            return {
              success: data.success !== undefined ? data.success : true,
              totalcount: data.totalcount || data.data?.length || 0,
              data: data.data || [],
            };
          },
          ...config,
        }
      );

      return (
        response || {
          success: false,
          totalcount: 0,
          data: [],
        }
      );
    } catch (error) {
      console.error(`Module ${moduleName} API error:`, error);
      return {
        success: false,
        totalcount: 0,
        data: [],
      };
    }
  },

  /**
   * Fetch available branches for current user using lookup endpoint
   * @param config - Optional request configuration
   * @returns Promise with array of branches
   */
  async getUserBranches(
    config?: RequestConfig
  ): Promise<Array<{ id: string; name: string }>> {
    try {
      console.log("[getUserBranches] Fetching branches using lookup endpoint...");

      // Use lookup endpoint for branches (same as used in forms)
      // getLookupData returns LookupOption[] with { label, value } structure
      const lookupOptions = await this.getLookupData(
        "branches",
        {
          parentid: "",
          lookuptype: "Select",
        },
        {
          includeAuthToken: true,
          errorHandling: "return-empty",
          ...config,
        }
      );

      console.log("[getUserBranches] Lookup options received:", lookupOptions);

      // Transform LookupOption[] to Branch[]
      // LookupOption has { label, value, ...otherProps }
      // Branch needs { id, name }
      const branches: Array<{ id: string; name: string }> = lookupOptions
        .filter((option) => option && option.value !== undefined && option.label)
        .map((option) => ({
          id: String(option.value),
          name: String(option.label),
        }));

      console.log("[getUserBranches] Transformed to branches:", branches);

      return branches || [];
    } catch (error) {
      console.error(`[getUserBranches] API error:`, error);
      return [];
    }
  },

  /**
   * Fetch workflow configuration
   * @param workflowName - The name of the workflow (e.g., "service")
   * @param config - Optional request configuration
   * @returns Promise with the workflow configuration
   */
  async getWorkflow(
    workflowName: string,
    config?: RequestConfig
  ): Promise<any> {
    try {
      const response = await this.get<any>(
        `/pabws/workflow`,
        { name: workflowName },
        {
          errorHandling: "return-empty",
          transformResponse: (data: any) => {
            // Extract workflow data from response
            if (data?.data) {
              return data.data;
            }
            return data;
          },
          ...config,
        }
      );

      return response || null;
    } catch (error) {
      console.error(`Workflow ${workflowName} API error:`, error);
      return null;
    }
  },

  /**
   * Fetch data for a submodule
   * @param subModuleName - The name of the submodule
   * @param recordId - The parent record ID
   * @param config - Optional request configuration
   * @returns Promise with the submodule data
   */
  async getSubModuleData(
    subModuleName: string,
    recordId: string | number,
    config?: RequestConfig
  ): Promise<any[]> {
    try {
      const endpoint = `/pabws/submodule/${subModuleName}/${recordId}`;
      // Scope lookup_items submodules (e.g. vehicle make/type) to login branch.
      const branchId = String(resolveLoginUser()?.branch_id ?? "").trim();
      const params = branchId ? { branch_id: branchId } : undefined;
      if (
        import.meta.env.DEV &&
        String(subModuleName).toLowerCase() === "purchase_details"
      ) {
        console.log("[PUR139 Submodule] getSubModuleData_request", {
          endpoint,
          subModuleName,
          recordId: String(recordId),
          branchId: branchId || null,
        });
      }
      const response = await this.get<any[]>(endpoint, params, {
        errorHandling: "return-empty",
        transformResponse: (data: any) => {
          if (!data) return [];

          let finalData: any = data;

          if (
            typeof data === "string" &&
            data.length > 10 &&
            !data.trim().startsWith("{")
          ) {
            try {
              const decrypted = authService.decryptData(data);
              if (decrypted) finalData = decrypted;
            } catch (e) {
              console.warn(
                `[getSubModuleData] Failed to decrypt raw string for ${subModuleName}`,
                e
              );
            }
          } else if (
            data &&
            typeof data === "object" &&
            typeof data.data === "string" &&
            data.data.length > 10 &&
            !data.data.trim().startsWith("{")
          ) {
            try {
              const decrypted = authService.decryptData(data.data);
              if (decrypted) finalData = decrypted;
            } catch (e) {
              console.warn(
                `[getSubModuleData] Failed to decrypt data for ${subModuleName}`,
                e
              );
            }
          } else if (data && typeof data === "object" && data.data !== undefined) {
            finalData = data.data;
          }

          if (Array.isArray(finalData)) return finalData;

          if (finalData && typeof finalData === "object") {
            if (Array.isArray(finalData.data)) return finalData.data;
            const arrayValue = Object.values(finalData).find((v) =>
              Array.isArray(v)
            );
            if (arrayValue) return arrayValue as any[];
            return [finalData];
          }

          return [];
        },
        ...config,
      });

      if (
        import.meta.env.DEV &&
        String(subModuleName).toLowerCase() === "purchase_details"
      ) {
        console.log("[PUR139 Submodule] getSubModuleData_response", {
          subModuleName,
          recordId: String(recordId),
          rowCount: Array.isArray(response) ? response.length : 0,
        });
      }

      return response || [];
    } catch (error) {
      console.error(`Submodule ${subModuleName} API error:`, error);
      return [];
    }
  },
};
